// Catalog Rules — sitewide CSS injection for Hide Price + Hide ATC.
//
// v1.1 architecture (refactor of the v1.0 Hide Price Mode that
// shipped with CTA injection): the two features run as independent
// rules read from shop_config. For each rule:
//   1. Resolve the merchant's HideRules block via resolveRules()
//   2. Detect the current page (product / collection / cart / search)
//   3. On product pages, extract product meta from
//      window.ShopifyAnalytics.meta.product → run shouldHideForProduct
//      → set data-qs-hide-{price,atc}="1" on <html> if matched
//   4. On collection / search pages, iterate per-card markup → for
//      each card extract per-card meta (vendor + tags from
//      data-* attributes that themes commonly emit) → match → set
//      data-qs-hide-{price,atc}-target="1" on the matched card
//   5. On cart page, fetch /cart.js → run matcher per line item →
//      if ANY line item triggers Hide ATC, also set
//      data-qs-cart-hide-atc on <html> so the Checkout button hides
//   6. MutationObserver re-runs steps 3-5 when DOM changes (theme
//      AJAX collection pagination, infinite scroll, cart-drawer
//      opens) so dynamically-loaded content gets targeted too
//
// NO CTA injection — the merchant's existing Request Quote button
// app block is the path forward for customers. This keeps the
// storefront CTA rendering as part of the merchant's design system
// instead of an inline-styled DOM patch we control.
//
// Best-effort per-card matching: themes that don't expose vendor /
// tags on card markup get skipped silently (no broken-looking
// half-hidden cards). Documented in the theme-compatibility
// callout in the admin UI.

import {
  resolveRules,
  shouldHideForProduct,
  type HideRules,
  type ProductMeta,
} from "@/lib/storefront-product-matcher";
import { AUTO_PLACE_BTN_ATTR, PAYMENT_CLUSTER_SELECTORS } from "./product-button";

// ── Pure helpers (DOM-free, testable) ─────────────────────────────

export interface PageContext {
  isProductPage: boolean;
  isCollectionPage: boolean;
  isCartPage: boolean;
  isSearchPage: boolean;
}

/** Path-based page classification. Pulled out so the page-type
 *  detection is testable without a DOM and so the embed and any
 *  future SSR consumer can share the same logic. */
export function classifyPath(pathname: string | null | undefined): PageContext {
  const p = (pathname || "/").toLowerCase().replace(/\/+$/, "") || "/";
  const isProductPage = /^\/products\/[^/]+/.test(p);
  // /cart and /cart/X (but not /cart/add which is a POST endpoint
  // some themes intercept — we don't want to engage there).
  const isCartPage = (p === "/cart") || (p.startsWith("/cart/") && !/^\/cart\/add/.test(p));
  const isCollectionPage = /^\/collections\//.test(p);
  const isSearchPage = p === "/search";
  return { isProductPage, isCollectionPage, isCartPage, isSearchPage };
}

/** Build the v1.1 stylesheet. Two scoping layers per feature:
 *    - html[data-qs-hide-*="1"] — sitewide for the current page
 *      (set when the product on a /products/<handle> page matches,
 *      or when the cart contains any line item that matches Hide ATC)
 *    - [data-qs-hide-*-target="1"] — per-element scope for collection /
 *      search grids (set on each card that matches the rule)
 *
 *  The price selector list intentionally guards .money with
 *  :not(input):not(option):not([data-qs-keep-price]) so form inputs
 *  (tip / donation widgets) + admin escape-hatched nodes stay
 *  visible. */
export function buildCatalogRulesCss(): string {
  // Selector unions shared between html-scope and per-card-scope.
  const PRICE_SELECTORS = [
    ".price",
    ".price-item",
    ".price-item--regular",
    ".price-item--sale",
    ".price__regular",
    ".price__sale",
    ".price__last",
    ".compare-at-price",
    ".price__compare",
    ".price--compare",
    ".unit-price",
    "[data-price]",
    "[data-product-price]",
    "[data-regular-price]",
    "[data-sale-price]",
    ".product-price",
    ".product__price",
    ".product-single__price",
    ".product-single__prices",
    ".product-form__info-content .price",
    ".cart-item__price",
    ".cart-item__totals",
    ".cart__total",
    ".totals__total-value",
    ".totals__subtotal-value",
    ".cart__subtotal",
    ".money:not(input):not(option):not([data-qs-keep-price])",
  ];

  // Every hide-ATC selector is exempt from matching OUR OWN auto-place
  // button (E2.1 root cause): a theme_button clone of a theme's own ATC
  // classes (add-to-cart / product-form__submit / …) would otherwise
  // get hidden by this SAME generated CSS the moment a merchant turns
  // on Hide ATC — the button hiding itself. data-qs-autoplace is our
  // identity attribute (see product-button.ts's AUTO_PLACE_BTN_ATTR) —
  // never present on a theme's own markup, so the exemption can't
  // accidentally un-hide a real ATC control.
  const ATC_SELECTORS = [
    '[name="add"]',
    "button.product-form__submit",
    'button[type="submit"][name="add"]',
    ".product-form__cart-submit",
    ".btn-product-add",
    ".product-single__add-to-cart",
    '[class*="add-to-cart"]',
    ...PAYMENT_CLUSTER_SELECTORS,
  ].map(s => `${s}:not([${AUTO_PLACE_BTN_ATTR}])`);

  const CART_CHECKOUT_SELECTORS = [
    ".cart__submit",
    ".cart__checkout",
    ".cart__checkout-button",
    '[name="checkout"]',
    "[data-checkout-button]",
  ];

  const expand = (prefix: string, selectors: string[]) =>
    selectors.map(s => `${prefix} ${s}`).join(",\n");

  return [
    `/* QuoteSense — Catalog Rules CSS. Generated by`,
    `   src/embed/catalog-rules.ts. Edit there, not in the rendered DOM. */`,

    // Hide Price — sitewide (when the current product on a product
    // page matches the rule) + per-card (collection / search grid).
    `${expand('html[data-qs-hide-price="1"]', PRICE_SELECTORS)},`,
    `${expand('[data-qs-hide-price-target="1"]', PRICE_SELECTORS)} {`,
    `  display: none !important;`,
    `  visibility: hidden !important;`,
    `}`,

    // Hide ATC — sitewide (product page match) + per-card +
    // cart-page checkout (when any line item triggers).
    `${expand('html[data-qs-hide-atc="1"]', ATC_SELECTORS)},`,
    `${expand('[data-qs-hide-atc-target="1"]', ATC_SELECTORS)} {`,
    `  display: none !important;`,
    `}`,
    `${expand('html[data-qs-cart-hide-atc="1"]', CART_CHECKOUT_SELECTORS)} {`,
    `  display: none !important;`,
    `}`,
  ].join("\n");
}

// ── DOM-side activator ────────────────────────────────────────────

/** Shape the embed reads off the public shop-config endpoint. All
 *  12 fields are optional in transit — resolveRules() fills in
 *  defaults so the matcher always sees a complete shape. */
export interface CatalogRulesConfig {
  hide_prices_enabled?: boolean;
  hide_prices_page_scope?: "all" | "product_pages_only";
  hide_prices_vendor_mode?: "any" | "include" | "exclude";
  hide_prices_vendors?: string[];
  hide_prices_tag_mode?: "any" | "include" | "exclude";
  hide_prices_tags?: string[];
  hide_prices_combinator?: "any" | "all";
  hide_atc_enabled?: boolean;
  hide_atc_page_scope?: "all" | "product_pages_only";
  hide_atc_vendor_mode?: "any" | "include" | "exclude";
  hide_atc_vendors?: string[];
  hide_atc_tag_mode?: "any" | "include" | "exclude";
  hide_atc_tags?: string[];
  hide_atc_combinator?: "any" | "all";
}

const STYLE_TAG_ID = "qs-catalog-rules-style";
const HTML_ATTR_HIDE_PRICE = "data-qs-hide-price";
const HTML_ATTR_HIDE_ATC = "data-qs-hide-atc";
const HTML_ATTR_CART_HIDE_ATC = "data-qs-cart-hide-atc";
const CARD_ATTR_HIDE_PRICE = "data-qs-hide-price-target";
const CARD_ATTR_HIDE_ATC = "data-qs-hide-atc-target";
const SCANNED_MARKER = "data-qs-catalog-scanned";

/** Extract a HideRules block from the merchant's shop-config
 *  payload — one for Hide Price, one for Hide Add to Cart. */
function readPriceRules(cfg: CatalogRulesConfig): HideRules {
  return resolveRules({
    enabled:     cfg.hide_prices_enabled,
    page_scope:  cfg.hide_prices_page_scope,
    vendor_mode: cfg.hide_prices_vendor_mode,
    vendors:     cfg.hide_prices_vendors,
    tag_mode:    cfg.hide_prices_tag_mode,
    tags:        cfg.hide_prices_tags,
    combinator:  cfg.hide_prices_combinator,
  });
}
function readAtcRules(cfg: CatalogRulesConfig): HideRules {
  return resolveRules({
    enabled:     cfg.hide_atc_enabled,
    page_scope:  cfg.hide_atc_page_scope,
    vendor_mode: cfg.hide_atc_vendor_mode,
    vendors:     cfg.hide_atc_vendors,
    tag_mode:    cfg.hide_atc_tag_mode,
    tags:        cfg.hide_atc_tags,
    combinator:  cfg.hide_atc_combinator,
  });
}

/** Top-level activator. Idempotent — safe to call multiple times.
 *  Boots early in the page lifecycle (no React, no app mount —
 *  just plain DOM mutations) so the price hider lands before
 *  theme paint when possible. */
export function applyCatalogRules(cfg: CatalogRulesConfig): void {
  if (typeof document === "undefined") return;
  const priceRules = readPriceRules(cfg);
  const atcRules = readAtcRules(cfg);
  if (!priceRules.enabled && !atcRules.enabled) return;

  injectStyleTag();
  const page = classifyPath(typeof window !== "undefined" ? window.location.pathname : "/");

  // Product page — async because Dawn (and many other OS 2.0
  // themes) DON'T expose product.tags via
  // window.ShopifyAnalytics.meta.product. We fetch the canonical
  // product JSON from `/products/{handle}.js` and fall back to
  // ShopifyAnalytics on fetch failure. Fire-and-forget — the
  // void here keeps applyCatalogRules sync at the call-site so
  // boot's existing two-phase activation (cache + network) flows
  // through unchanged.
  if (page.isProductPage) {
    void activateProductPage(priceRules, atcRules);
  }

  // Cart page — fetch cart and check if any line item triggers
  // Hide ATC. If so, hide the cart-page Checkout button. Hide
  // Price applies to the cart line item prices independently via
  // the sitewide html[data-qs-hide-price="1"] when any rule allows.
  if (page.isCartPage) {
    // For a cart-page activation we want Hide Price to apply when
    // ANY line item matches (so a basket containing matched items
    // doesn't reveal their price in the cart summary).
    void activateCartPage(priceRules, atcRules);
  }

  // Cart page — fetch cart and check if any line item triggers
  // Hide ATC. If so, hide the cart-page Checkout button. Hide
  // Price applies to the cart line item prices independently via
  // the sitewide html[data-qs-hide-price="1"] when any rule allows.
  if (page.isCartPage) {
    // For a cart-page activation we want Hide Price to apply when
    // ANY line item matches (so a basket containing matched items
    // doesn't reveal their price in the cart summary).
    void activateCartPage(priceRules, atcRules);
  }

  // Per-card targeting on collection / search / homepage product
  // grids. Runs now + MutationObserver covers theme-AJAX'd content.
  if (priceRules.enabled || atcRules.enabled) {
    scanProductCards(priceRules, atcRules, page);
    setupObserver(priceRules, atcRules, page);
  }
}

/** Inject (or refresh) the <style> tag carrying the catalog-rules
 *  CSS. Idempotent — replaces the existing tag if present so a
 *  later activation picks up updated content (we don't currently
 *  re-emit but the contract is forward-compatible). */
function injectStyleTag(): void {
  let tag = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null;
  if (!tag) {
    tag = document.createElement("style");
    tag.id = STYLE_TAG_ID;
    document.head.appendChild(tag);
  }
  tag.textContent = buildCatalogRulesCss();
}

function setHtmlAttr(attr: string): void {
  try { document.documentElement.setAttribute(attr, "1"); } catch { /* sandboxed */ }
}

/** Extract product handle from a /products/<handle>[…] path.
 *  Pulled out so the test suite can pin the regex behavior
 *  without a window. */
export function productHandleFromPath(pathname: string | null | undefined): string | null {
  const m = String(pathname || "").match(/\/products\/([^/?#]+)/i);
  return m ? m[1] : null;
}

/** Normalize a parsed product JSON shape (from /products/{handle}.js
 *  OR window.ShopifyAnalytics.meta.product) into a ProductMeta.
 *  Pure — testable without a window. Handles both the array
 *  (canonical /products/x.js) and comma-string (ShopifyAnalytics)
 *  tag shapes. */
export function normalizeProductMeta(
  raw: { vendor?: any; tags?: any } | null | undefined,
  isProductPage: boolean,
): ProductMeta | null {
  if (!raw) return null;
  const tags = Array.isArray(raw.tags)
    ? raw.tags
    : typeof raw.tags === "string"
      ? raw.tags.split(",").map((t: string) => t.trim()).filter(Boolean)
      : [];
  return {
    vendor: String(raw.vendor ?? ""),
    tags: tags.map((t: any) => String(t)),
    isProductPage,
  };
}

/** Async meta-fetch for the current product page. Tries
 *  /products/{handle}.js first — that's the canonical source
 *  that consistently exposes both vendor + tags. Falls back to
 *  window.ShopifyAnalytics.meta.product when the fetch fails
 *  (offline / 404 / weird theme intercept).
 *
 *  Bug context: Dawn (and several other OS 2.0 themes) do NOT
 *  populate ShopifyAnalytics.meta.product.tags on product pages.
 *  Tag-based rules silently no-op'd before this fetch fallback
 *  landed. */
async function getProductMetadata(): Promise<ProductMeta | null> {
  if (typeof window === "undefined") return null;
  const handle = productHandleFromPath(window.location.pathname);
  if (!handle) return null;
  try {
    const r = await fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: "same-origin" });
    if (!r.ok) throw new Error(`/products/${handle}.js → ${r.status}`);
    const parsed = await r.json();
    const meta = normalizeProductMeta(parsed, true);
    if (meta) return meta;
    throw new Error("normalize returned null");
  } catch {
    // Fallback path — ShopifyAnalytics may have vendor but is
    // missing tags on Dawn. Better than nothing for vendor-only
    // rules; tag rules will still no-op on the fallback path.
    const sa = (window as any).ShopifyAnalytics?.meta?.product;
    return normalizeProductMeta(sa, true);
  }
}

/** Activate Hide Price / Hide ATC on the current /products/<handle>
 *  page. Async because the meta fetch is. Sets the html-scope data
 *  attrs once meta + rules are resolved. */
async function activateProductPage(priceRules: HideRules, atcRules: HideRules): Promise<void> {
  const currentProduct = await getProductMetadata();
  // Non-noisy debug surface — fires only when at least one rule
  // is enabled (gated by applyCatalogRules above) so an OFF-by-
  // default merchant never sees this in their storefront console.
  // Keys are short — hp / atc / vendor / page / tags — so a
  // quick console scan can spot misconfig (vendor mismatch, tag
  // not present, etc.) without rummaging through the config blob.
  try {
    // eslint-disable-next-line no-console
    console.debug("[QS] catalog-rules: applying", {
      hp: priceRules.enabled,
      atc: atcRules.enabled,
      vendor: currentProduct?.vendor ?? null,
      tags: currentProduct?.tags ?? null,
      page: "product",
    });
  } catch { /* defensive — console isn't always present */ }
  if (!currentProduct) return;
  if (priceRules.enabled && shouldHideForProduct(priceRules, currentProduct)) {
    setHtmlAttr(HTML_ATTR_HIDE_PRICE);
  }
  if (atcRules.enabled && shouldHideForProduct(atcRules, currentProduct)) {
    setHtmlAttr(HTML_ATTR_HIDE_ATC);
  }
}

/** Scan product cards on the current page + apply per-card target
 *  attributes when they match. Uses a wide selector union so the
 *  card-discovery covers Dawn (.card-wrapper / .grid__item) +
 *  Sense (.product-card) + Refresh / older themes (.grid-product). */
function scanProductCards(priceRules: HideRules, atcRules: HideRules, page: PageContext): void {
  const selectors = [
    ".card-wrapper",
    ".product-card",
    ".grid__item",
    ".grid-product",
    "[data-product-card]",
    "[data-product-id]",
  ].join(", ");
  document.querySelectorAll<HTMLElement>(selectors).forEach(card => {
    // Skip already-scanned cards (idempotency for MutationObserver).
    if (card.hasAttribute(SCANNED_MARKER)) return;
    card.setAttribute(SCANNED_MARKER, "1");
    // First try local extraction (data-* attrs or embedded JSON).
    // If the theme doesn't expose vendor / tags on cards, fall
    // back to fetching /products/<handle>.js using the link the
    // card almost certainly carries — same per-page-load cache
    // as the product page activator so multiple cards for the
    // same product (e.g. featured + recommended slots) hit
    // network once.
    const local = readCardProductMeta(card, page);
    if (local) {
      applyMatchedAttrs(card, local, priceRules, atcRules);
      return;
    }
    const handle = extractCardProductHandle(card);
    if (!handle) return;
    void fetchProductJson(handle).then(meta => {
      if (!meta) return;
      const enriched = normalizeProductMeta(meta, page.isProductPage);
      if (!enriched) return;
      applyMatchedAttrs(card, enriched, priceRules, atcRules);
    });
  });
}

function applyMatchedAttrs(
  card: HTMLElement,
  meta: ProductMeta,
  priceRules: HideRules,
  atcRules: HideRules,
): void {
  if (priceRules.enabled && shouldHideForProduct(priceRules, meta)) {
    card.setAttribute(CARD_ATTR_HIDE_PRICE, "1");
  }
  if (atcRules.enabled && shouldHideForProduct(atcRules, meta)) {
    card.setAttribute(CARD_ATTR_HIDE_ATC, "1");
  }
}

/** Per-page-load product JSON cache. /products/<handle>.js is
 *  cheap but a collection page can render dozens of cards for the
 *  same handle (featured-product blocks, recommended slots,
 *  recently-viewed strips). Single network roundtrip per handle. */
const _productJsonCache = new Map<string, Promise<{ vendor?: any; tags?: any } | null>>();
async function fetchProductJson(handle: string): Promise<{ vendor?: any; tags?: any } | null> {
  if (typeof window === "undefined") return null;
  const cached = _productJsonCache.get(handle);
  if (cached) return cached;
  const p = (async () => {
    try {
      const r = await fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: "same-origin" });
      if (!r.ok) return null;
      return await r.json();
    } catch { return null; }
  })();
  _productJsonCache.set(handle, p);
  return p;
}

/** Extract a product handle from a card's anchor links. Most
 *  themes wrap the card in an <a href="/products/<handle>"> or
 *  include such a link in the card body. Falls back to data-*
 *  attributes some themes emit. */
export function extractCardProductHandle(card: HTMLElement): string | null {
  // data-product-handle is the cleanest signal when present.
  const dataHandle = card.getAttribute("data-product-handle");
  if (dataHandle) return dataHandle;
  // Anchor href — typical Dawn / Sense card structure.
  const link = card.querySelector<HTMLAnchorElement>('a[href*="/products/"]');
  if (link) {
    const handle = productHandleFromPath(link.getAttribute("href") || "");
    if (handle) return handle;
  }
  return null;
}

/** Per-card meta extraction. Themes expose vendor / tags in a few
 *  different shapes; we try each in order and stop on the first
 *  hit. Returns null when nothing's available — that card silently
 *  stays unmatched (better than half-hiding a card we can't
 *  classify reliably). */
function readCardProductMeta(card: HTMLElement, page: PageContext): ProductMeta | null {
  // Direct data-* attributes (some Dawn forks, custom themes).
  const datasetVendor = card.getAttribute("data-product-vendor")
    || card.getAttribute("data-vendor")
    || "";
  const datasetTagsStr = card.getAttribute("data-product-tags")
    || card.getAttribute("data-tags")
    || "";
  if (datasetVendor || datasetTagsStr) {
    return {
      vendor: datasetVendor,
      tags: datasetTagsStr.split(",").map(t => t.trim()).filter(Boolean),
      // Card scan implies we're on a non-product page (collection /
      // search). isProductPage stays false even though the underlying
      // product is theoretically on a /products/<handle> page —
      // the matcher's page_scope check sees the CURRENT page type.
      isProductPage: page.isProductPage,
    };
  }
  // Embedded JSON (common pattern: <script type="application/json"
  // data-product-json>…</script> inside the card).
  const jsonNode = card.querySelector<HTMLScriptElement>('script[type="application/json"][data-product-json]')
    || card.querySelector<HTMLScriptElement>('script[type="application/json"]');
  if (jsonNode) {
    try {
      const parsed = JSON.parse(jsonNode.textContent || "{}");
      const vendor = String(parsed.vendor ?? "");
      const tags = Array.isArray(parsed.tags)
        ? parsed.tags.map((t: any) => String(t))
        : typeof parsed.tags === "string"
          ? parsed.tags.split(",").map((t: string) => t.trim()).filter(Boolean)
          : [];
      if (vendor || tags.length) {
        return { vendor, tags, isProductPage: page.isProductPage };
      }
    } catch { /* malformed JSON; fall through */ }
  }
  return null;
}

let _observer: MutationObserver | null = null;
function setupObserver(priceRules: HideRules, atcRules: HideRules, page: PageContext): void {
  if (_observer) return;
  if (typeof MutationObserver === "undefined") return;
  _observer = new MutationObserver(() => {
    scanProductCards(priceRules, atcRules, page);
  });
  _observer.observe(document.body, { childList: true, subtree: true });
}

/** Cart-page activation. Reads /cart.js (Shopify's public cart
 *  JSON endpoint, no auth needed) → if Hide ATC applies to any
 *  line item, hide the Checkout button. Hide Price applies
 *  globally for the cart page if ANY line item triggers the
 *  rule (we set the sitewide html[data-qs-hide-price] so all the
 *  cart line item price selectors hide together). */
async function activateCartPage(priceRules: HideRules, atcRules: HideRules): Promise<void> {
  try {
    const r = await fetch("/cart.js", { credentials: "same-origin" });
    if (!r.ok) return;
    const cart = await r.json();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    let anyPriceMatch = false;
    let anyAtcMatch = false;
    for (const it of items) {
      const meta: ProductMeta = {
        vendor: String(it.vendor ?? ""),
        // Shopify's /cart.js exposes per-line product tags + vendor
        // (since 2024+); older themes may have a stripped-down
        // payload — those won't match here but the rule still
        // applies sitewide if the merchant enabled scope=all.
        tags: Array.isArray(it.product_tags) ? it.product_tags : [],
        isProductPage: false, // cart isn't a product page
      };
      if (priceRules.enabled && shouldHideForProduct(priceRules, meta)) anyPriceMatch = true;
      if (atcRules.enabled && shouldHideForProduct(atcRules, meta)) anyAtcMatch = true;
    }
    if (anyPriceMatch) setHtmlAttr(HTML_ATTR_HIDE_PRICE);
    if (anyAtcMatch) setHtmlAttr(HTML_ATTR_CART_HIDE_ATC);
  } catch {
    // Cart fetch failed — leave cart page as-is. Merchant's
    // theme-side Request Quote button is still rendered.
  }
}
